import * as React from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import { ArrowLeft as ArrowLeftIcon } from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import {
  AppSEO,
  NotFound,
  ServerError,
} from "@hl/shared-features/lib/features/layout";
import CopyButton from "@hl/shared-features/lib/features/widgets/CopyButton";
import { networkLookup } from "@hl/shared-features/lib/utils/blockExplorer";
import { maskAddress } from "@hl/shared-features/lib/utils/content";
import {
  AppShell,
  Button,
  Container,
  Divider,
  Grid,
  Group,
  Space,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { Link, useParams } from "react-router-dom";

import { mapCollectionToBlockchainEntity } from "components/BlockchainEntityTitle/types";
import { TOOLS_URL } from "config";
import { collectionTypeToDisplayName } from "~/features/collections/displayUtils";
import { useGetContractDetailQuery } from "~/features/contracts/queries.graphql.generated";
import { AppFooter } from "~/features/layout/Footer";
import { AppHeader } from "~/features/layout/Header";
import { useGetCollectionByUserPaginatedQuery } from "~/features/tokens/queries/collections.graphql.generated";

const ContractDetail = () => {
  const { contractId } = useParams();
  const {
    data: dataContract,
    loading: loadingContract,
    error,
  } = useGetContractDetailQuery({
    variables: {
      contractId: contractId || "",
    },
  });

  const { data: dataCollections, loading: loadingCollections } =
    useGetCollectionByUserPaginatedQuery({
      variables: {
        first: 9,
        cursor: null,
        search: null,
        sortBy: null,
        sortDirection: null,
        contractId: dataContract?.getContractById.id || null,
      },
      skip: !dataContract?.getContractById.id,
    });

  if (loadingContract || loadingCollections) {
    return <LoadingPage />;
  }

  if (!dataContract) {
    return <NotFound redirectUrl={TOOLS_URL.home} />;
  }

  if (error) {
    return <ServerError redirectUrl={TOOLS_URL.home} />;
  }

  const contract = dataContract.getContractById;
  const networkData = networkLookup(contract.chainId);

  const collections = dataCollections?.getCollectionByUserPaginated.collections;
  const usingOpenseaBlocklist = collections?.some(
    (collection) => collection.usingOpenseaBlocklist
  );
  return (
    <>
      <AppSEO title="Contract" />
      <AppShell
        header={
          <AppHeader
            showBrand={false}
            leftButtons={
              <Link to="/">
                <Button
                  color={SECONDARY_COLOR}
                  leftIcon={<ArrowLeftIcon height={12} />}
                >
                  Back
                </Button>
              </Link>
            }
          />
        }
        footer={<AppFooter />}
      >
        <Container size="lg">
          <Stack spacing={0}>
            <Title order={3}>{contract.name}</Title>
            <Group spacing={4}>
              <Text size="sm">{networkData.displayName}</Text>
              <Text size="sm">·</Text>
              <Text size="sm">{contract.contractType} (ERC-721)</Text>
              <Text size="sm">·</Text>
              <Text size="sm">{maskAddress(contract.address || "", 6, 4)}</Text>
              <CopyButton copyValue={contract.address || ""} />
            </Group>
          </Stack>
          <Space h={40} />
          <Grid grow gutter={80}>
            <Grid.Col span={5}>
              <Text size="xl" fw={500}>
                Details
              </Text>
              <BoxContainer radius="md" mt="xs">
                <Group position="apart">
                  <Text size="sm">Symbol</Text>
                  <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                    {contract.symbol}
                  </Text>
                </Group>
                <Divider my="md" />
                <Group position="apart">
                  <Text size="sm">Contract type</Text>
                  <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                    {contract.contractType} (ERC-721)
                  </Text>
                </Group>
                {usingOpenseaBlocklist && (
                  <>
                    <Divider my="md" />
                    <Group position="apart" noWrap>
                      <Text size="sm">
                        Add tokens created by this contract to the CORI operator
                        filter registry
                      </Text>
                      <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                        Yes
                      </Text>
                    </Group>
                  </>
                )}
              </BoxContainer>
            </Grid.Col>
            <Grid.Col span={4}>
              {collections?.length && (
                <>
                  <Text size="xl" fw={500}>
                    Collections
                  </Text>
                  <BoxContainer radius="md" mt="xs">
                    {collections.map((collection, index) => {
                      const entity =
                        mapCollectionToBlockchainEntity(collection);

                      const { mintStatus, entityType, collectionType } = entity;

                      const type =
                        collectionTypeToDisplayName(collectionType) ??
                        entityType;
                      return (
                        <>
                          <Group>
                            <ResizedImage
                              src={entity.details.image}
                              width={72}
                              height={72}
                              fit="contain"
                              radius="md"
                            />
                            <Stack spacing={0}>
                              <Text size="md" fw={500}>
                                {collection.name}
                              </Text>
                              <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                                {type} ·{" "}
                                {networkLookup(collection.chainId).displayName}
                              </Text>
                              {mintStatus && (
                                <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                                  {mintStatus.total === 0
                                    ? `${mintStatus.minted}`
                                    : `${mintStatus.minted} / ${mintStatus.total}`}{" "}
                                  minted
                                </Text>
                              )}
                            </Stack>
                          </Group>
                          {index + 1 < collections.length && (
                            <Divider my="md" />
                          )}
                        </>
                      );
                    })}
                  </BoxContainer>
                </>
              )}
            </Grid.Col>
          </Grid>
        </Container>
      </AppShell>
    </>
  );
};

export default ContractDetail;
